
import { mapState, mapGetters } from 'vuex'
import bankAccount from '~/mixins/common/bank-account'
import finance from '~/mixins/common/pages/finance'
export default {
    mixins: [finance, bankAccount],
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
        parentSteppers: {
            type: [String, Number],
            default: null,
        },
        parentResultData: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            steppers: 1,
            formValid: false,
            form: {
                amount: null,
            },
            existLoading: false,
            resultData: {},
            selectedBankId: null,
            pleaseSelectedBankId: false,
            amount: '0',
            displayBankAccountError: false,
        }
    },
    computed: {
        ...mapState({
            loading: (state) => state.themes.withdraw.loading,
            player: (state) => state.player.player,
            verifyCurrency: (state) => state.banks.verifyCurrency,
            operationSetting: (state) => state.settings.operationSetting,
        }),
        ...mapGetters({
            exchangeRates: 'themes/settings/exchangeRate',
        }),
        withdrawalMinAndMaxAmount() {
            // Since the Lao and international are having different API endpoints to check the min and max amount of withdrawal
            if (this.isInternational) {
                return {
                    min: parseFloat(this.operationSetting.withdraw_min || 0),
                    max: parseFloat(this.operationSetting.withdraw_max || 0),
                }
            }
            return {
                min: parseFloat(this.verifyCurrency.withdraw_min || 0),
                max: parseFloat(this.verifyCurrency.withdraw_max || 0),
            }
        },
        withdrawalAmountRule() {
            return [
                (v) =>
                    !!v || this.tr(this.FRONTEND_ERROR_CODES.amountIsRequired),
                (v) =>
                    parseFloat(v?.toString()?.replace(/,/g, '')) > 0 ||
                    `${this.tr(
                        this.FRONTEND_ERROR_CODES.amountGreaterThanZero
                    )}`,
                (v) =>
                    parseFloat(v?.toString()?.replace(/,/g, '')) <=
                        parseFloat(this.playerRealtimeBalance()) ||
                    `${this.tr(this.FRONTEND_ERROR_CODES.balanceInsufficient)}`,
                (v) =>
                    (v && parseFloat(v?.toString()?.replace(/,/g, ''))) >=
                        parseFloat(this.withdrawalMinAndMaxAmount?.min) ||
                    `${this.tr(
                        this.FRONTEND_ERROR_CODES.minimumWithdrawIs
                    )} ${this.formatCurrencyJS(
                        this.withdrawalMinAndMaxAmount.min
                    )}`,
                (v) =>
                    (v && parseFloat(v?.toString()?.replace(/,/g, ''))) <=
                        parseFloat(this.withdrawalMinAndMaxAmount?.max) ||
                    `${this.tr(
                        this.FRONTEND_ERROR_CODES.maximumWithdrawIs
                    )} ${this.formatCurrencyJS(
                        this.withdrawalMinAndMaxAmount.max
                    )}`,
                (v) =>
                    this.decimalRule(v) ||
                    this.tr(this.FRONTEND_ERROR_CODES.decimalIsTwoDigit),
            ]
        },
        primaryBankAccount() {
            const foundBankAccount =
                this.bankAccount?.player_bank_accounts?.find(
                    (account) => account.is_primary
                )
            return foundBankAccount || {}
        },
        exchangeRate() {
            const foundRate = this.exchangeRates.find(
                (rate) =>
                    rate.from_currency === this.player.currency &&
                    rate.to_currency === this.primaryBankAccount.currency
            )
            return foundRate ? foundRate.rate : 0
        },
        destinationCurrency() {
            if (this.bankAccount?.player_bank_accounts?.length > 0) {
                const bankCurrency =
                    this.bankAccount?.player_bank_accounts.find(
                        (el) => el?.id === this.selectedBankId
                    )
                if (bankCurrency) return bankCurrency.currency
                else return null
            }
            return null
        },
        exchangeRateBaseOnPlayerAndBankAccount() {
            const getMatchCurrency = this.exchangeRates.find((el) => {
                if (
                    el.from_currency === this.player.currency &&
                    el.to_currency === this.destinationCurrency
                ) {
                    return true
                }
                return false
            })
            if (getMatchCurrency) {
                return getMatchCurrency.rate
            }
            return 0
        },
    },
    watch: {
        'form.amount'(value) {
            if (value) {
                if (!this.verifyCurrency.withdraw_min) {
                    this.$store.dispatch(
                        'banks/verifyCurrency',
                        this.player.currency
                    )
                }
            }
        },
        player(value) {
            if (value?.currency) {
                this.$store.dispatch(
                    'banks/verifyCurrency',
                    this.player.currency
                )
            }
        },
        parentSteppers(value) {
            this.steppers = value
        },
        parentResultData: {
            deep: true,
            handler(value) {
                this.resultData = { ...value }
            },
        },
    },
    mounted() {
        this.$root.withdrawExistChange = (item) => {
            if (!item?.is_exist) {
                this.steppers = 1
                this.$emit('update:parentSteppers', 1)
                this.resultData = {}
            } else {
                this.steppers = 2
                this.$emit('update:parentSteppers', 2)
                this.resultData = { ...item }
            }
        }
        if (!this.isInternational && this.player?.currency) {
            this.$store.dispatch('banks/verifyCurrency', this.player.currency)
        }
    },
    beforeDestroy() {
        this.doCancelAxiosRequest()
    },
    destroyed() {
        this.$root.withdrawExistChange = null
    },
    methods: {
        async doWithdraw() {
            this.$refs?.refWithdraw?.validate()

            if (!this.selectedBankId) this.pleaseSelectedBankId = true
            if (!this.formValid || !this.selectedBankId) return

            this.form.player_bank_account_id = this.selectedBankId

            const confirm = window.confirm(this.tl('uSureToWithdraw?'))
            if (!confirm) return

            const result = await this.$store.dispatch(
                'themes/withdraw/createWithdraw',
                this.form
            )
            if (result) {
                this.resultData = result
                this.steppers = 2
                this.$emit('update:parentSteppers', 2)
                this.$refs?.refWithdraw?.reset()
            }
        },
        clearForm() {
            this.$refs?.refWithdraw?.reset()
        },
        async cancelPending(item) {
            const confirm = window.confirm(this.tl('uSureToCancel?'))
            if (!confirm) return
            const result = await this.$store.dispatch(
                'themes/withdraw/cancelWithdraw',
                item.id
            )
            if (result) {
                this.resultData = {}
                this.steppers = 1
                this.$emit('update:parentSteppers', 1)
            }
        },
        async checkIfWithdrawalExist() {
            if (this.player?.has_bank_account) {
                this.existLoading = true
                const result = await this.$store.dispatch(
                    'themes/withdraw/checkExistWithdrawal'
                )
                if (result?.is_exist) {
                    this.resultData = result
                    this.steppers = 2
                }
                this.existLoading = false
            }
        },
        onInputAmount(amount) {
            if (isNaN(amount?.replace(/,/g, ''))) {
                this.form.amount = '0'
                return
            }
            this.amount = amount
                ? Number.parseFloat(amount?.replace(/,/g, ''))?.toLocaleString()
                : ''
            this.form.amount = this.amount?.toString()?.replace(/,/g, '')
        },
    },
}
